import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { employeeAPI } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import PermissionGate from '../PermissionGate';
import ConfirmationModal from '../ConfirmationModal';

const Employees = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const queryClient = useQueryClient();
  const searchInputRef = useRef(null);
  const { Permission } = usePermissions();

  // Debounce the search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Track cursor position and focus state
  const [cursorPosition, setCursorPosition] = useState(0);
  const [shouldMaintainFocus, setShouldMaintainFocus] = useState(false);

  // Maintain focus when search results update
  useEffect(() => {
    if (shouldMaintainFocus && searchInputRef.current) {
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          const position = Math.min(cursorPosition, searchTerm.length);
          searchInputRef.current.setSelectionRange(position, position);
        }
      }, 10);

      return () => clearTimeout(timer);
    }
  }, [debouncedSearchTerm, shouldMaintainFocus, cursorPosition, searchTerm.length]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCursorPosition(e.target.selectionStart);
    setShouldMaintainFocus(true);
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    setShouldMaintainFocus(true);
  };

  // Handle search input blur
  const handleSearchBlur = () => {
    setShouldMaintainFocus(false);
  };

  const { data: employeesData, isLoading, error } = useQuery({
    queryKey: ['employees', currentPage, debouncedSearchTerm],
    queryFn: () => {
      if (debouncedSearchTerm) {
        return employeeAPI.search(debouncedSearchTerm).then(res => res.data);
      }
      return employeeAPI.getAll(currentPage * itemsPerPage, itemsPerPage, true).then(res => res.data);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => employeeAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['employees']);
      setShowDeleteModal(false);
    },
  });

  const handleDelete = (id, employeeName) => {
    setDeleteTarget({ id, employeeName });
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
    }
  };

  const employees = Array.isArray(employeesData) ? employeesData : (employeesData?.employees || []);
  const totalCount = employeesData?.total || employees.length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-300">Error loading employees: {error.message}</p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Employees</h1>
          <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">Manage employee information and records</p>
        </div>
        <PermissionGate permission={Permission.CREATE_EMPLOYEE}>
          <Link
            to="/employees/new"
            className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-800 transition-colors"
          >
            <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Employee</span>
            <span className="sm:hidden">Add</span>
          </Link>
        </PermissionGate>
      </div>

      {/* Search */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-500 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
            placeholder="Search employees by name, ID, designation, or department..."
          />
        </div>
      </div>

      {/* Employees List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <h2 className="text-base sm:text-lg font-semibold text-white">
            {totalCount} Employee{totalCount !== 1 ? 's' : ''}
          </h2>
        </div>

        {employees.length === 0 ? (
          <div className="px-4 sm:px-6 py-8 text-center">
            <UserIcon className="mx-auto h-12 w-12 text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-white">No employees found</h3>
            <p className="mt-1 text-sm text-gray-400">
              {debouncedSearchTerm ? 'Try adjusting your search' : 'Get started by adding a new employee'}
            </p>
            <PermissionGate permission={Permission.CREATE_EMPLOYEE}>
              {!debouncedSearchTerm && (
                <div className="mt-6">
                  <Link
                    to="/employees/new"
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-gray-800"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Add Employee
                  </Link>
                </div>
              )}
            </PermissionGate>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Employee
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Designation
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Department
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Salary
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Joined
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {employees.map((employee) => (
                    <tr key={employee._id || employee.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {employee.first_name?.[0]}{employee.last_name?.[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <Link
                              to={`/employees/${employee._id || employee.id}`}
                              className="text-sm font-medium text-white hover:text-blue-400 transition-colors"
                            >
                              {employee.first_name} {employee.last_name}
                            </Link>
                            <div className="text-sm text-gray-400">
                              {employee.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{employee.designation}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">{employee.department || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white font-medium">{formatCurrency(employee.salary)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatDate(employee.date_of_joining)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <PermissionGate permission={Permission.UPDATE_EMPLOYEE}>
                            <Link
                              to={`/employees/${employee._id || employee.id}/edit`}
                              className="text-blue-400 hover:text-blue-300 transition-colors"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </Link>
                          </PermissionGate>
                          <PermissionGate permission={Permission.DELETE_EMPLOYEE}>
                            <button
                              onClick={() => handleDelete(employee._id || employee.id, `${employee.first_name} ${employee.last_name}`)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="sm:hidden divide-y divide-gray-700">
              {employees.map((employee) => (
                <div key={employee._id || employee.id} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {employee.first_name?.[0]}{employee.last_name?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <Link
                          to={`/employees/${employee._id || employee.id}`}
                          className="text-sm font-medium text-white hover:text-blue-400 transition-colors"
                        >
                          {employee.first_name} {employee.last_name}
                        </Link>
                        <div className="text-xs text-gray-400">{employee.employee_id}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <PermissionGate permission={Permission.UPDATE_EMPLOYEE}>
                        <Link
                          to={`/employees/${employee._id || employee.id}/edit`}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                      </PermissionGate>
                      <PermissionGate permission={Permission.DELETE_EMPLOYEE}>
                        <button
                          onClick={() => handleDelete(employee._id || employee.id, `${employee.first_name} ${employee.last_name}`)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </PermissionGate>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-400">Designation:</span>
                      <div className="text-white">{employee.designation}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Department:</span>
                      <div className="text-white">{employee.department || '-'}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Salary:</span>
                      <div className="text-white font-medium">{formatCurrency(employee.salary)}</div>
                    </div>
                    <div>
                      <span className="text-gray-400">Joined:</span>
                      <div className="text-white">{formatDate(employee.date_of_joining)}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-400">{employee.email}</div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {!debouncedSearchTerm && totalCount > itemsPerPage && (
              <div className="bg-gray-900 px-4 py-3 flex items-center justify-between border-t border-gray-700 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={(currentPage + 1) * itemsPerPage >= totalCount}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-400">
                      Showing <span className="font-medium">{currentPage * itemsPerPage + 1}</span> to{' '}
                      <span className="font-medium">{Math.min((currentPage + 1) * itemsPerPage, totalCount)}</span> of{' '}
                      <span className="font-medium">{totalCount}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                        disabled={currentPage === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-600 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={(currentPage + 1) * itemsPerPage >= totalCount}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-600 bg-gray-800 text-sm font-medium text-gray-400 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Employee"
        message={`Are you sure you want to delete ${deleteTarget?.employeeName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default Employees;
